import React, { useState, useMemo } from 'react';
import { X, Zap } from 'lucide-react';
import './GenerateConfirmModal.css';

const QW_TYPE_MAP: Record<string, string> = {
  social_media:     'social_media_posts',
  meta_data:        'meta_data',
  ad_copy:          'ad_copy_variations',
  keyword_research: 'keyword_research',
};

interface GenerateConfirmModalProps {
  type: string;
  label: string;
  streamNames: string[];
  allTasks: any[] | null;      // null = still loading
  initialStreams: string[];
  onConfirm: (selectedStreams: string[]) => void;
  onCancel: () => void;
}

const GenerateConfirmModal: React.FC<GenerateConfirmModalProps> = ({
  type, label, streamNames, allTasks, initialStreams, onConfirm, onCancel,
}) => {
  const [selectedStreams, setSelectedStreams] = useState<string[]>(initialStreams);

  const { toGenerate, skipped } = useMemo(() => {
    if (!allTasks) return { toGenerate: null, skipped: 0 };

    let articles = allTasks.filter(t =>
      t.status === 'completed' &&
      t.synthesized_result &&
      !(t.input_data && typeof t.input_data === 'object' && 'quick_win_type' in t.input_data)
    );

    if (selectedStreams.length > 0) {
      articles = articles.filter(t => {
        const stream = t.input_data?.masterplan_stream;
        return stream && selectedStreams.includes(stream);
      });
    }

    const backendType = QW_TYPE_MAP[type];
    let skipCount = 0;
    if (type === 'hero_images') {
      skipCount = articles.filter((t: any) =>
        t.output_data && typeof t.output_data === 'object' && t.output_data.images
      ).length;
    } else if (backendType) {
      const alreadyHave = new Set(
        allTasks
          .filter(t => t.input_data?.quick_win_type === backendType)
          .map(t => t.input_data?.source_task_id)
          .filter(Boolean)
      );
      skipCount = articles.filter(t => alreadyHave.has(t.task_id)).length;
    }

    return { toGenerate: articles.length - skipCount, skipped: skipCount };
  }, [allTasks, selectedStreams, type]);

  const isNewsletter = type === 'email_newsletter';
  const isLoading = allTasks === null;
  const nothingToDo = !isLoading && !isNewsletter && toGenerate === 0;

  const toggleStream = (s: string) =>
    setSelectedStreams(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);

  const streamLabel = selectedStreams.length === 0
    ? 'all streams'
    : selectedStreams.length === 1
    ? `1 stream`
    : `${selectedStreams.length} streams`;

  return (
    <div className="gcm-overlay" onClick={onCancel}>
      <div className="gcm-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="gcm-header">
          <h3 className="gcm-title">Generate {label}</h3>
          <button className="gcm-close" onClick={onCancel} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        {/* Stream selector */}
        {streamNames.length > 1 && (
          <div className="gcm-section">
            <div className="gcm-section-label">Filter by Stream</div>
            <div className="gcm-stream-list">
              {streamNames.map(s => (
                <label key={s} className="gcm-stream-option">
                  <input
                    type="checkbox"
                    checked={selectedStreams.includes(s)}
                    onChange={() => toggleStream(s)}
                  />
                  <span>{s}</span>
                </label>
              ))}
            </div>
            {selectedStreams.length > 0 && (
              <button className="gcm-clear-link" onClick={() => setSelectedStreams([])}>
                Clear — use all streams
              </button>
            )}
          </div>
        )}

        {/* Summary */}
        <div className="gcm-summary">
          {isLoading ? (
            <span className="gcm-summary-loading">Calculating…</span>
          ) : isNewsletter ? (
            <p className="gcm-summary-text">
              <strong>1 newsletter</strong> will be generated from articles in{' '}
              <strong>{streamLabel}</strong>.
            </p>
          ) : nothingToDo ? (
            <p className="gcm-summary-none">
              All articles in {streamLabel} already have {label.toLowerCase()} generated.
              Select a different stream or clear the filter.
            </p>
          ) : (
            <>
              <p className="gcm-summary-text">
                <strong>{toGenerate} article{toGenerate !== 1 ? 's' : ''}</strong> will be
                generated across <strong>{streamLabel}</strong>.
              </p>
              {skipped > 0 && (
                <p className="gcm-summary-skip">
                  {skipped} article{skipped !== 1 ? 's' : ''} already generated — will be skipped.
                </p>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="gcm-footer">
          <button className="gcm-btn-cancel" onClick={onCancel}>Cancel</button>
          <button
            className="gcm-btn-confirm"
            onClick={() => onConfirm(selectedStreams)}
            disabled={isLoading || nothingToDo}
          >
            <Zap size={14} />
            Generate
          </button>
        </div>
      </div>
    </div>
  );
};

export default GenerateConfirmModal;
